<div class="business-profiles-page">
  @if (isEditingInfo) {
    <app-business-info-edit
      [businessProfile]="businessProfile"
      (saved)="onEditSaved($event)"
      (cancelled)="isEditingInfo = false"
    ></app-business-info-edit>
  } @else {
    <!-- CTA header -->
    <div class="cta-container">
      <div class="cta-content">
        <span class="cta-badge">Business funding profile</span>
        <h2 class="cta-title">Manage Your Business Needs</h2>
        <p class="cta-description">
          Keep your business profile current so Organizations can quickly
          understand your location, funding needs, and support priorities.
        </p>
      </div>

      @if (hasNoProfile) {
        <div class="cta-action">
          <button
            class="create-profile-btn"
            (click)="openCreateWizard()"
            type="button"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
            >
              <path
                d="M12 5V19M5 12H19"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            Create Profile
          </button>
        </div>
      }
    </div>

    <!-- Loading skeleton -->
    @if (isLoading) {
      <div class="profiles-section">
        <app-skeleton [type]="'business-profiles'"></app-skeleton>
      </div>
    }

    <!-- Error state -->
    @else if (hasError) {
      <div class="profiles-section">
        <div class="state-message state-error">
          <svg
            class="state-icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
              fill="currentColor"
            />
          </svg>
          <div class="state-text">
            <p class="state-title">Unable to load profile</p>
            <p class="state-desc">
              Something went wrong while fetching your business profile.
            </p>
          </div>
          <button class="retry-btn" (click)="loadBusinessProfile()">
            Retry
          </button>
        </div>
      </div>
    }

    <!-- No profile yet (404) -->
    @else if (hasNoProfile) {
      <div class="profiles-section">
        <div class="no-profile-text">
          <p class="no-profile-title">No business profile yet</p>
          <p class="no-profile-desc">
            You haven't created a business profile yet. Click
            <strong>Create Profile</strong> in the header above to get started.
          </p>
        </div>
      </div>
    }

    <!-- Profile loaded -->
    @else if (businessProfile) {
      <!-- Assignments & Connections Stats Section -->
      <div class="basic-info-section connection-stats-section">
        <div class="profiles-header">
          <h3>Assignments &amp; Stats</h3>
        </div>

        <div class="connection-stats-grid">
          <!-- Assignees card -->
          <div
            class="stats-card"
            id="tour-assignees"
            [class.has-open-tooltip]="activeTooltip === 'assignees'"
          >
            <div class="stats-card-header">
              <span class="info-label">Assignees</span>
              <div class="metric-info-wrap">
                <button
                  class="info-btn"
                  type="button"
                  aria-label="More info"
                  (click)="toggleTooltip('assignees', $event)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
                    />
                  </svg>
                </button>
                @if (activeTooltip === "assignees") {
                  <div class="info-tooltip">
                    <span class="tooltip-title">Assignees</span>
                    <p>
                      Organizations that have been assigned to review and manage
                      your business profile on this platform.
                    </p>
                  </div>
                }
              </div>
            </div>

            @if (
              businessProfile.assignees && businessProfile.assignees.length > 0
            ) {
              <div class="assignee-bubbles-row">
                <div class="assignee-bubbles">
                  @for (
                    assignee of businessProfile.assignees.slice(0, 4);
                    track assignee.id;
                    let i = $index
                  ) {
                    <div class="bubble-wrap" [style.z-index]="10 - i">
                      @if (assignee.cdfi_logo_path) {
                        <img
                          class="bubble-avatar bubble-img"
                          [src]="getImageUrl(assignee.cdfi_logo_path)"
                          alt="logo"
                        />
                      } @else {
                        <div class="bubble-avatar bubble-initial">
                          {{
                            (
                              assignee.display_name ||
                              assignee.connection_name ||
                              "A"
                            )
                              .charAt(0)
                              .toUpperCase()
                          }}
                        </div>
                      }
                      <div class="bubble-tooltip">
                        <span class="bubble-tooltip-name">{{
                          assignee.display_name || assignee.connection_name
                        }}</span>
                        @if (assignee.assigned_on_utc) {
                          <span class="bubble-tooltip-date"
                            >Assigned:
                            {{
                              assignee.assigned_on_utc | date: "MMM d, y"
                            }}</span
                          >
                        }
                      </div>
                    </div>
                  }
                  @if (businessProfile.assignees.length > 4) {
                    <div class="bubble-avatar bubble-extra" style="z-index: 6">
                      +{{ businessProfile.assignees.length - 4 }}
                    </div>
                  }
                </div>
                <button
                  class="view-details-btn"
                  type="button"
                  (click)="showAssigneesPopup = true"
                >
                  <span>View Details</span>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </button>
              </div>
              <span class="assignee-count-label">
                {{ businessProfile.assignees.length }}
                {{
                  businessProfile.assignees.length === 1
                    ? "organization"
                    : "organizations"
                }}
                assigned
              </span>
            } @else {
              <span class="info-value no-data">No active assignees</span>
            }
          </div>

          <!-- Matched Connections Card -->
          <div
            class="stats-card"
            id="tour-matched-connections"
            [class.has-open-tooltip]="activeTooltip === 'matched-connections'"
          >
            <div class="stats-card-header">
              <span class="info-label">Matched Organizations</span>
              <div class="metric-info-wrap">
                <button
                  class="info-btn"
                  type="button"
                  aria-label="More info"
                  (click)="toggleTooltip('matched-connections', $event)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
                    />
                  </svg>
                </button>
                @if (activeTooltip === "matched-connections") {
                  <div class="info-tooltip">
                    <span class="tooltip-title">Matched Organizations</span>
                    <p>
                      Organizations whose lending criteria closely match your
                      business profile based on your products, location, and
                      other attributes.
                    </p>
                  </div>
                }
              </div>
            </div>

            @if (
              businessProfile.matched_record_connections &&
              businessProfile.matched_record_connections.length > 0
            ) {
              <div class="assignee-bubbles-row">
                <div class="assignee-bubbles">
                  @for (
                    conn of businessProfile.matched_record_connections.slice(
                      0,
                      4
                    );
                    track conn.id;
                    let i = $index
                  ) {
                    <div
                      class="bubble-avatar bubble-initial bubble-green"
                      [title]="conn.display_name || conn.connection_name"
                      [style.z-index]="10 - i"
                    >
                      {{
                        (conn.display_name || conn.connection_name || "C")
                          .charAt(0)
                          .toUpperCase()
                      }}
                    </div>
                  }
                  @if (businessProfile.matched_record_connections.length > 4) {
                    <div class="bubble-avatar bubble-extra" [style.z-index]="6">
                      +{{
                        businessProfile.matched_record_connections.length - 4
                      }}
                    </div>
                  }
                </div>

                <button
                  type="button"
                  class="view-matches-btn"
                  (click)="navigateToMatches()"
                  id="tour-view-matches"
                >
                  <span>View Matches</span>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </button>
              </div>
              <span class="assignee-count-label">
                {{ businessProfile.matched_record_connections.length }}
                {{
                  businessProfile.matched_record_connections.length === 1
                    ? "match"
                    : "matches"
                }}
                found
              </span>
            } @else {
              <div class="no-matches-row">
                <span class="info-value no-data">No matched organizations</span>
                <button
                  type="button"
                  class="view-matches-btn"
                  (click)="navigateToMatches()"
                  id="tour-view-matches"
                >
                  <span>View Matches</span>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </button>
              </div>
            }
          </div>

          <!-- Profile views count card -->
          <div
            class="stats-card"
            id="tour-organization-views"
            [class.has-open-tooltip]="activeTooltip === 'org-views'"
          >
            <div class="stats-card-header">
              <span class="info-label">Business Views</span>
              <div class="metric-info-wrap">
                <button
                  class="info-btn"
                  type="button"
                  aria-label="More info"
                  (click)="toggleTooltip('org-views', $event)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
                    />
                  </svg>
                </button>
                @if (activeTooltip === "org-views") {
                  <div class="info-tooltip">
                    <span class="tooltip-title">Business Views</span>
                    <p>
                      Total number of times this business profile has been
                      viewed — broken down by individual users and the
                      organizations they belong to.
                    </p>
                  </div>
                }
              </div>
            </div>

            <div class="views-dual-row">
              <div class="views-stat-block">
                <span class="views-big-number">{{
                  businessProfile.views?.user_view_count || 0
                }}</span>
                <span class="views-stat-label">
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width="13"
                    height="13"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"
                    />
                  </svg>
                  Users
                </span>
              </div>
              <div class="views-divider"></div>
              <div class="views-stat-block">
                <span class="views-big-number views-big-number--org">{{
                  businessProfile.views?.cdfi_view_count || 0
                }}</span>
                <span class="views-stat-label">
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width="13"
                    height="13"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"
                    />
                  </svg>
                  Orgs
                </span>
              </div>
            </div>
            <!-- <p class="views-sub-desc">
              {{ businessProfile.views?.user_view_count || 0 }} users from
              {{ businessProfile.views?.cdfi_view_count || 0 }} organizations
              viewed this profile
            </p> -->
          </div>
        </div>
      </div>

      <!-- Basic Information Section -->
      @if (
        basicInformationProperties.length > 0 || applicationSummaryProperty
      ) {
        <div class="basic-info-section" id="tour-basic-info">
          <div class="profiles-header basic-info-header">
            <h3>Business Information</h3>
            <button
              id="tour-edit-info"
              class="edit-info-btn"
              (click)="navigateToEditInfo()"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              Edit
            </button>
          </div>

          <!-- Application Summary (taking full width first) -->
          @if (applicationSummaryProperty && applicationSummaryHtml) {
            <div class="app-summary-container">
              <span class="info-label">{{
                applicationSummaryProperty.property_name
              }}</span>
              <div
                #summaryEl
                class="summary-text-wrapper"
                [class.expanded]="isSummaryExpanded"
                [innerHTML]="applicationSummaryHtml"
              ></div>
              @if (showSummaryToggle) {
                <button
                  class="summary-toggle-btn"
                  (click)="toggleSummary()"
                  type="button"
                >
                  {{ isSummaryExpanded ? "Show Less" : "Show More" }}
                </button>
              }
            </div>
          }

          <div class="basic-info-grid">
            @for (prop of basicInformationProperties; track prop.property) {
              @if (
                prop.value !== null &&
                prop.value !== undefined &&
                prop.value !== "" &&
                !(isHasBusinessNotYes && prop.property === "company_name")
              ) {
                <div class="info-item">
                  <span class="info-label">
                    {{
                      isHasBusinessNotYes &&
                      prop.property === "business_address"
                        ? "Address"
                        : prop.property_name
                    }}
                  </span>
                  <span class="info-value">{{
                    formatPropertyValue(prop)
                  }}</span>
                </div>
              }
            }
          </div>
        </div>
      }

      <!-- Products & Services Section -->
      <div class="profiles-section" id="tour-products-services">
        <div class="profiles-header">
          <h3>Your Products &amp; Services of Interest</h3>
          <button
            class="edit-info-btn"
            (click)="toggleProductsEdit()"
            id="tour-edit-products"
            type="button"
          >
            @if (isEditingProducts) {
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 13l4 4L19 7"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              Done
            } @else {
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              Edit
            }
          </button>
        </div>

        @if (interestedProducts.length > 0) {
          <div class="interested-products-grid">
            @for (product of interestedProducts; track product) {
              <div
                class="product-card"
                [class.edit-mode]="isEditingProducts"
                [attr.tabindex]="isEditingProducts ? -1 : 0"
                [attr.role]="!isEditingProducts ? 'button' : null"
                (click)="!isEditingProducts && openProductDetails(product)"
                (keydown.enter)="
                  !isEditingProducts && openProductDetails(product)
                "
                (keydown.space)="
                  !isEditingProducts && openProductDetails(product)
                "
              >
                @if (isEditingProducts) {
                  <button
                    class="delete-product-btn"
                    (click)="confirmDeleteProduct($event, product)"
                    type="button"
                    aria-label="Remove product"
                    title="Remove"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path
                        d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                      ></path>
                    </svg>
                  </button>
                }
                <div class="product-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path [attr.d]="getProductIcon(product)" />
                  </svg>
                </div>
                <div class="product-content">
                  <h4 class="product-title">{{ product }}</h4>
                  <p class="product-desc">
                    @if (!isEditingProducts) {
                      {{
                        isChamberOrNetworking(product)
                          ? "Click to view"
                          : "Click to view and update details"
                      }}
                    }
                  </p>
                </div>
                @if (!isEditingProducts) {
                  <div class="product-arrow">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 18l6-6-6-6"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                }
              </div>
            }

            <!-- Add product card: visible when there are still available products -->
            @if (availableProducts.length > 0) {
              <div
                class="product-card add-product-card"
                role="button"
                tabindex="0"
                (click)="openAddPanel()"
                (keydown.enter)="openAddPanel()"
                (keydown.space)="openAddPanel()"
                title="Add Product/Service"
              >
                @if (isAddingProduct) {
                  <div class="add-spinner"></div>
                } @else {
                  <div class="product-icon add-product-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 5v14M5 12h14"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                      />
                    </svg>
                  </div>
                  <div class="product-content">
                    <h4 class="product-title">Add Product/Service</h4>
                    <p class="product-desc">Click to add more</p>
                  </div>
                }
              </div>
            }
          </div>
        } @else {
          <div class="no-profile-text">
            <p class="no-profile-title">No interested products selected</p>
            <p class="no-profile-desc">
              Update your profile to add products and services you're interested
              in.
            </p>
            @if (availableProducts.length > 0) {
              <div class="empty-state-actions">
                <button
                  class="add-product-btn-empty"
                  (click)="openAddPanel()"
                  type="button"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                  >
                    <path
                      d="M12 5v14M5 12h14"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                  Add Products
                </button>
              </div>
            }
          </div>
        }

        <!-- Add-product picker panel -->
        @if (showAddPanel) {
          <div
            class="add-product-panel-backdrop"
            (click)="closeAddPanel()"
            (keydown.enter)="closeAddPanel()"
            (keydown.space)="closeAddPanel()"
            tabindex="0"
            role="button"
            aria-label="Close panel"
          ></div>
          <div
            class="add-product-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            <div class="add-product-panel-header">
              <span id="modal-title"
                >Select Products &amp; Services of Interest</span
              >
              <button
                class="close-panel-btn"
                (click)="closeAddPanel()"
                type="button"
                aria-label="Close"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
            <div class="panel-warning-box">
              <svg
                class="panel-warning-icon"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span>
                <strong>Notice:</strong> Adding products or services will
                trigger a new organization matching process, overwriting
                previous match requests.
              </span>
            </div>
            <div class="add-product-panel-list">
              @for (name of availableProducts; track name) {
                <div
                  class="add-product-option"
                  [class.selected]="isProductSelected(name)"
                  (click)="toggleProductSelection(name)"
                  (keydown.enter)="toggleProductSelection(name)"
                  (keydown.space)="toggleProductSelection(name)"
                  tabindex="0"
                  role="checkbox"
                  [attr.aria-checked]="isProductSelected(name)"
                >
                  <div class="checkbox-wrapper">
                    <input
                      type="checkbox"
                      [checked]="isProductSelected(name)"
                      (click)="$event.stopPropagation()"
                      (change)="toggleProductSelection(name)"
                      tabindex="-1"
                      class="product-checkbox"
                    />
                  </div>
                  <div class="option-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path [attr.d]="getProductIcon(name)" />
                    </svg>
                  </div>
                  <span class="option-name">{{ name }}</span>
                </div>
              }
            </div>
            <div class="add-product-panel-footer">
              <button
                class="panel-btn-cancel"
                (click)="closeAddPanel()"
                type="button"
              >
                Cancel
              </button>
              <button
                class="panel-btn-save"
                (click)="addSelectedProducts()"
                [disabled]="
                  selectedProductsToAdd.length === 0 || isAddingProduct
                "
                type="button"
              >
                @if (isAddingProduct) {
                  <div class="save-spinner"></div>
                } @else {
                  Add Selected ({{ selectedProductsToAdd.length }})
                }
              </button>
            </div>
          </div>
        }
      </div>

      <!-- Business Documents Section -->
      <app-business-documents></app-business-documents>

      <!-- Delete Business Section -->
      <div class="delete-business-section" id="tour-delete-business">
        <div class="delete-business-card">
          <div class="delete-business-info">
            <div class="delete-business-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
              >
                <path
                  d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M10 11v6M14 11v6"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
              </svg>
            </div>
            <div>
              <h4 class="delete-business-title">Delete Business Profile</h4>
              <p class="delete-business-desc">
                Permanently remove your business profile and all associated
                data. This action cannot be undone.
              </p>
            </div>
          </div>
          <button
            class="delete-business-btn"
            type="button"
            (click)="confirmDeleteBusiness()"
          >
            Delete Profile
          </button>
        </div>
      </div>
    }
  }
</div>

@if (showDeleteBusinessPopup) {
  <app-popup
    heading="Delete Business Profile"
    confirmText="Delete"
    cancelText="Cancel"
    [isConfirmDisabled]="isDeletingBusiness"
    (cancelClicked)="cancelDeleteBusiness()"
    (confirmClicked)="deleteBusiness()"
  >
    <p>
      Are you sure you want to permanently delete your business profile?<br /><br />
      All associated data including your business information, attached
      documents, products, and services will be removed and
      <strong>cannot be recovered</strong>.
    </p>
  </app-popup>
}

@if (isRecordDetailsPopupOpen && businessProfile) {
  <app-record-details
    [company]="companyForPopup"
    (popupClosed)="closeRecordDetailsPopup()"
    (companyDeleted)="onCompanyDeleted()"
    (companyDetailsUpdated)="onCompanyDetailsUpdated($event)"
  ></app-record-details>
}

@if (showCreateWizard) {
  <app-create-profile-wizard
    (closed)="closeCreateWizard()"
    (profileCreated)="onProfileCreated($event)"
  ></app-create-profile-wizard>
}

@if (productToDelete) {
  <app-popup
    heading="Remove Product"
    confirmText="Remove"
    cancelText="Cancel"
    [isConfirmDisabled]="isDeletingProduct"
    (cancelClicked)="cancelDeleteProduct()"
    (confirmClicked)="deleteProduct()"
  >
    <div class="product-confirm-popup-content">
      <p>
        Are you sure you want to remove
        <strong>{{ productToDelete }}</strong> from your Products &amp; Services
        of Interest?
      </p>
      <div class="popup-warning-callout">
        <svg
          class="popup-warning-icon"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span>
          <strong>Please note:</strong> This will clear all associated details
          for this product and trigger a new organization matching process,
          overwriting your previous match requests.
        </span>
      </div>
    </div>
  </app-popup>
}

@if (showChamberPopup) {
  <app-popup
    heading="Chamber of Commerce &amp; Networking"
    confirmText="Got It"
    cancelText="Close"
    [showCloseIcon]="true"
    (cancelClicked)="showChamberPopup = false"
    (confirmClicked)="showChamberPopup = false"
  >
    <div class="chamber-info-popup-content">
      <div class="chamber-info-icon">
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
          />
        </svg>
      </div>
      <p class="chamber-info-text">
        This is listed under your products and services of interest because you
        indicated that you are interested in joining a local chamber of commerce
        or other regional business networking organizations.
      </p>
    </div>
  </app-popup>
}

<!-- Assignees Detail Popup -->
@if (showAssigneesPopup && businessProfile) {
  <div
    class="detail-popup-backdrop"
    role="button"
    tabindex="0"
    aria-label="Close"
    (click)="showAssigneesPopup = false"
    (keydown.enter)="showAssigneesPopup = false"
    (keydown.escape)="showAssigneesPopup = false"
  ></div>
  <div
    class="detail-popup"
    role="dialog"
    aria-modal="true"
    aria-labelledby="assignees-popup-title"
  >
    <div class="detail-popup-header">
      <span id="assignees-popup-title" class="detail-popup-title"
        >Assignees</span
      >
      <button
        class="close-panel-btn"
        type="button"
        aria-label="Close"
        (click)="showAssigneesPopup = false"
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M18 6L6 18M6 6l12 12"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
    <div class="detail-popup-body">
      @for (assignee of businessProfile.assignees ?? []; track assignee.id) {
        <div
          class="detail-popup-row detail-popup-row--clickable"
          role="button"
          tabindex="0"
          (click)="navigateToCdfiProfile(assignee.connection_name)"
          (keydown.enter)="navigateToCdfiProfile(assignee.connection_name)"
          (keydown.space)="navigateToCdfiProfile(assignee.connection_name)"
        >
          <div class="detail-popup-avatar-wrap">
            @if (assignee.cdfi_logo_path) {
              <img
                class="detail-popup-logo"
                [src]="getImageUrl(assignee.cdfi_logo_path)"
                alt="logo"
              />
            } @else {
              <div class="detail-popup-avatar">
                {{
                  (assignee.display_name || assignee.connection_name || "A")
                    .charAt(0)
                    .toUpperCase()
                }}
              </div>
            }
          </div>
          <div class="detail-popup-info">
            <span class="detail-popup-name">
              {{ assignee.display_name || assignee.connection_name }}
            </span>
            <div class="detail-popup-meta">
              @if (assignee.assigned_on_utc) {
                <div class="detail-meta-item">
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width="13"
                    height="13"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z"
                    />
                  </svg>
                  <span
                    >Assigned:
                    {{
                      assignee.assigned_on_utc | date: "MMM d, y, h:mm a"
                    }}</span
                  >
                </div>
              }
              @if (
                assignee.lead_status !== null &&
                assignee.lead_status !== undefined
              ) {
                <div class="detail-meta-item">
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width="13"
                    height="13"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                    />
                  </svg>
                  <span class="detail-meta-label">Status:</span>
                  <span
                    class="detail-meta-status"
                    [class.status-active]="
                      assignee.lead_status === 7 ||
                      assignee.lead_status === 9 ||
                      assignee.lead_status === 10 ||
                      assignee.lead_status === 11
                    "
                  >
                    {{ getLeadStatusLabel(assignee.lead_status) }}
                  </span>
                </div>
              }
              @if (
                assignee.approved_loan_amount !== null &&
                assignee.approved_loan_amount !== undefined
              ) {
                <div class="detail-meta-item detail-meta-loan">
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width="13"
                    height="13"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"
                    />
                  </svg>
                  <span>Approved Loan:</span>
                  <span class="detail-meta-amount"
                    >${{ assignee.approved_loan_amount | number }}</span
                  >
                </div>
              }
            </div>
          </div>
          <div class="detail-popup-nav-arrow">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 18l6-6-6-6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>
      }
    </div>
  </div>
}
